import * as Sentry from "@sentry/react";
import { useEffect, useMemo } from "react";
import { useQuery } from "react-query";
import { useSelector } from "react-redux";

import { EMPTY_FILTER_ID } from "constants/app.constants";
import { RootState } from "store/rootReducer";
import type { FocusTranslationStatus } from "utils/color/focusColorSync/buildFocusColors";
import { normalizeMatchKey } from "utils/color/focusColorSync/operatorKeys";
import { getFocusSyncTrigger } from "utils/color/focusColorSync/triggers";

import { fetchFocusColorSyncTranslation } from "api/data";

export interface UseFocusTranslationParams {
  // The focus property currently in effect. A request is only ever made
  // when this is one of the three translating trigger properties (see
  // triggers.ts) — Header.Operator and Header.ResourcePlay never fetch.
  property: Maybe<string>;
  // External gate (the beta feature flag) — defaults to true so callers
  // that have nothing to gate on can omit it.
  enabled?: boolean;
}

export interface FocusTranslationResult {
  // normalized variant value -> public-record operator name. undefined
  // while unresolved (not fetched, still fetching, or not needed) —
  // deliberately distinct from an already-resolved (possibly empty) map.
  translations: Maybe<ReadonlyMap<string, string>>;
  // normalized variant value -> every public-record name seen for it -> how many
  // wells carry that pair. Colours need one name per variant; bins need all of
  // them. Same gating as translations.
  allTranslations: Maybe<ReadonlyMap<string, ReadonlyMap<string, number>>>;
  // Callers need to tell "still coming" from "never coming": only the former
  // is a reason to hold the colours currently on screen.
  status: FocusTranslationStatus;
}

/**
 * Translates one non-public-record operator variant (short name, exchange
 * stock symbol, stock symbol) to the public-record operator name a rig or
 * frac record always carries. Cached (React Query) by filter and property,
 * so panning or zooming the map never refetches; only a filter re-apply or a
 * focus change does — the filter's `lastUpdated` timestamp is part of the
 * query key, the same precedent useDataQuery's queryKey follows, so a
 * re-applied filter with an unchanged id still busts the cache.
 */
export function useFocusTranslation(
  params: UseFocusTranslationParams
): FocusTranslationResult {
  const { property, enabled = true } = params;

  const trigger = getFocusSyncTrigger(property);
  const needsTranslation = enabled && trigger?.needsTranslation === true;

  // The selector closes over needsTranslation and returns undefined when
  // this instance has nothing to translate, rather than reading
  // state.filter.filterId unconditionally — this hook runs from every
  // chart's legend, so an unconditional read would re-render all of them on
  // every filter apply, even ones nowhere near a rig/frac operator-variant
  // focus.
  const filterId = useSelector((state: RootState) =>
    needsTranslation ? state.filter?.filterId : undefined
  );
  const hasFilter = !!filterId?.id && filterId.id !== EMPTY_FILTER_ID;
  const isEnabled = needsTranslation && hasFilter;

  const { data, isFetching, isLoading, isError, isPreviousData } = useQuery(
    ["focusColorSyncTranslation", filterId, property],
    () =>
      fetchFocusColorSyncTranslation({
        filterId: filterId?.id as string,
        variantProperty: property as string
      }),
    {
      enabled: isEnabled,
      staleTime: Infinity,
      refetchOnWindowFocus: false,
      // Switching between two variant trigger fields changes the query key.
      // Without this, react-query would drop `data` to undefined for the
      // whole in-flight window — exactly the "not ready" shape that already
      // holds colouring at its last known good state, so this just avoids
      // manufacturing that window when it doesn't need to.
      keepPreviousData: true,
      // A 400 (missing/invalid query params) or any other request failure
      // will never succeed on retry, and the default retries would make a
      // permanent backend failure look identical to a slow request from the
      // outside — report it instead of failing invisibly into "focus stays
      // pending forever".
      retry: false,
      onError: (error: unknown) => {
        Sentry.withScope((scope) => {
          scope.setLevel("warning");
          scope.setContext("focusColorSyncTranslation", {
            property,
            filterId: filterId?.id
          });
          Sentry.captureException(error);
        });
      }
    }
  );

  const translationMaps = useMemo(() => {
    if (!data || !property) {
      return undefined;
    }
    // keepPreviousData serves the last response while a new one is in flight.
    // That previous response can belong to another property OR to another
    // filter, and neither is visible in the data itself — one well's pair of
    // values looks perfectly valid whichever property or filter produced it.
    // Trusting it would report a resolved mapping that is quietly wrong, so
    // treat anything react-query flags as previous as unresolved.
    if (isPreviousData) {
      return undefined;
    }

    // First-wins on a collision, walking the wells in the order the backend
    // returned them. A blank value on either side carries no usable
    // translation and is skipped outright — otherwise a well with a blank
    // public-record name would win first-wins for its variant and permanently
    // discard a later well's real name for that same key.
    const map = new Map<string, string>();
    const allNames = new Map<string, Map<string, number>>();
    for (const { variantValue, publicRecordValue } of data) {
      if (!variantValue.trim() || !publicRecordValue.trim()) {
        continue;
      }
      const key = normalizeMatchKey(variantValue);
      if (!map.has(key)) {
        map.set(key, publicRecordValue);
      }
      // One row per well, so counting rows counts the wells behind each pair.
      let names = allNames.get(key);
      if (!names) {
        names = new Map<string, number>();
        allNames.set(key, names);
      }
      names.set(publicRecordValue, (names.get(publicRecordValue) ?? 0) + 1);
    }

    return { map, allNames };
    // isPreviousData earns its place here: with keepPreviousData react-query
    // hands back the SAME data object while the new request is out, so on a
    // filter change (property unchanged) it is the only dependency that moves.
    // Without it the memo returns the previous filter's mapping and reports it
    // as resolved.
  }, [data, property, isPreviousData]);

  const resolvedTranslations = isEnabled ? translationMaps?.map : undefined;
  const allTranslations = isEnabled ? translationMaps?.allNames : undefined;

  // Every well came back unusable. That is a real answer for a filter whose
  // wells genuinely carry no variant name, but it is also what asking for a
  // field that does not exist looks like, and the two are worth telling apart
  // in hindsight. The resolution treats it as "no matches" either way.
  //
  // Reported from an effect rather than while building the map above: a memo is
  // a cache, not a promise to run once, so reporting there fires again on any
  // recompute and twice over in StrictMode, which is indistinguishable from a
  // failure that is genuinely repeating.
  useEffect(() => {
    if (!resolvedTranslations || resolvedTranslations.size > 0 || !data?.length) {
      return;
    }
    Sentry.withScope((scope) => {
      scope.setLevel("warning");
      scope.setContext("focusColorSyncTranslation", {
        property,
        wellCount: data.length
      });
      Sentry.captureMessage("Focus colour sync translation matched no wells");
    });
  }, [resolvedTranslations, data, property]);

  const status: FocusTranslationStatus = useMemo(() => {
    if (!isEnabled) {
      return "not-needed";
    }
    if (isError) {
      return "failed";
    }
    if (resolvedTranslations != null) {
      return "resolved";
    }
    if (isLoading || isFetching) {
      return "loading";
    }
    // Enabled, nothing in flight, no error, and still nothing usable — there
    // is nothing left to wait for, so report it as failed rather than leaving
    // callers holding their colours indefinitely.
    return "failed";
  }, [isEnabled, isError, isFetching, isLoading, resolvedTranslations]);

  return { translations: resolvedTranslations, allTranslations, status };
}
